/* eslint-disable no-nested-ternary */
import React, { FunctionComponent } from 'react';
import { Box } from '@mui/material';
import AccessibilityNewIcon from '@mui/icons-material/AccessibilityNew';
import { useParams } from 'react-router';
import Layout from '../../../components/Layout/Layout';
import Pagebar from '../../../components/Pagebar/Pagebar';
import PlanStats from './components/PlanStats/PlanStats';
import StatsController from './controller/stats.controller';
import ExercisesController from './controller/exercises.controller';
import AthleteStats from './components/AthleteStats/AthleteStats';
import ExerciseStats from './components/ExerciseStats/ExerciseStats';
import LineChart from '../../../components/Charts/Line/LineChart';
import ExerciseStatsSelectorButton from './components/ExerciseStats/ExerciseStatsSelectorButton';
import Text from '../../../components/CustomTextFormatter/Text';
import { StatToDisplayEnum } from '../../../common/enums/StatsToDisplayEnum';
import { AverageLoadPerExerciseStat, iconStyle, StatsContainer } from './PTReports.style';
import UserStatsController from './controller/user.controller';
import { PATH_CONSTANTS } from '../../../helper/constants';
import SlidingPopup from '../../../components/SlidingPopup/SlidingPopup';
import LoaderSpinner from '../../../components/LoaderSpinner/LoaderSpinner';
import ErrorPopup from '../../../components/ErrorPopup/ErrorPopup';

interface OwnProps {}

type Props = OwnProps;

const PTReports: FunctionComponent<Props> = () => {
  const { userId } = useParams();

  const {
    data,
    period,
    setPeriod,
    averageLoadsPerMuscleGroupsStats,
    activePlanVolumes,
    statsLoading,
    setApiStatsError,
    apiStatsError,
  } = StatsController(userId || '');

  const {
    searching,
    onChange,
    resetSearch,
    exerciseToDisplay,
    exerciseLoading,
    onClickExercise,
    showStats,
    exerciseByDateChart,
    setStatToDisplay,
    statToDisplay,
    averagePerExerciseStat,
    setShowStats,
    apiExerciseError,
    setApiExerciseError,
    onClickSearchButton,
  } = ExercisesController(userId || '');

  const {
    dataWeight,
    dataMeasures,
    loading,
    userStats,
    dataWeightOpt,
    dataMeasuresOpt,
    apiUserError,
    setUserApiError,
  } = UserStatsController(userId || '');

  const { ATHLETES_PT_PATH } = PATH_CONSTANTS;

  if (exerciseLoading || loading || statsLoading) return <LoaderSpinner />;

  return (
    <Layout>
      <>
        <Pagebar specificPath={`${ATHLETES_PT_PATH}/${userId}`} indexPage title="Report" />

        <AthleteStats
          dataWeightOpt={dataWeightOpt}
          dataMeasuresOpt={dataMeasuresOpt}
          dataWeight={dataWeight}
          dataMeasures={dataMeasures}
          maximal={userStats}
        />

        <PlanStats
          data={data}
          period={period}
          setPeriod={setPeriod}
          averageLoadPerMuscleGroup={averageLoadsPerMuscleGroupsStats}
          activePlanVolumes={activePlanVolumes}
        />

        <ExerciseStats
          exerciseToDisplay={exerciseToDisplay}
          searching={searching}
          onChange={onChange}
          resetSearch={resetSearch}
          onClickSearchButton={onClickSearchButton}
          onClickExercise={onClickExercise}
        />

        <SlidingPopup
          open={showStats}
          handleClose={() => {
            setShowStats(false);
          }}
          additionalStyle={{ height: '30vh' }}
        >
          <StatsContainer>
            <ExerciseStatsSelectorButton statToDisplay={statToDisplay} setStatToDisplay={setStatToDisplay} />

            {statToDisplay === StatToDisplayEnum.PER_DATE && (<LineChart options={dataWeightOpt} data={exerciseByDateChart} />
            )}

            {statToDisplay === StatToDisplayEnum.TOTAL && (
            <AverageLoadPerExerciseStat>
              <Box sx={{
                background: 'rgba(255, 255, 255, 0.19)',
                borderRadius: '0.7rem',
                width: '4rem',
                height: '4rem',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              >
                <AccessibilityNewIcon sx={iconStyle} />
              </Box>
              <Text type="h1">
                {`${averagePerExerciseStat.toFixed().toString()} kg`}
              </Text>
            </AverageLoadPerExerciseStat>
            )}
          </StatsContainer>
        </SlidingPopup>

        <ErrorPopup
          open={apiStatsError !== '' || apiExerciseError !== '' || apiUserError !== ''}
          handleClose={() => {
            setApiStatsError('');
            setApiExerciseError('');
            setUserApiError('');
          }}
          title="An error occurred :("
          caption={apiStatsError !== '' ? apiStatsError : apiExerciseError !== '' ? apiExerciseError : apiUserError}
          actions
        />
      </>
    </Layout>
  );
};

export default PTReports;
